import { AsyncPipe, DOCUMENT, NgClass, NgStyle } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivationEnd } from '@angular/router';

import { EventDetailsColumnType, EventModel, isBespokeSport } from '@frontend/sports/betting-offer/feature/model';
import { ShortNameModule, ShortTeamNamesService } from '@frontend/sports/betting-offer/feature/short-team-names';
import { MediaConfig, ScoreboardBackgroundConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { Optional } from '@frontend/sports/common/core/utils/extended-types';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { ScrollContainerService } from '@frontend/sports/common/core/utils/scroll-container';
import { CompetitionLogosService } from '@frontend/sports/competition-logos/feature';
import { MediaTab } from '@frontend/sports/grid/media/feature/model';
import { IMediaRootState, MediaState, mediaStateSelector } from '@frontend/sports/grid/media/feature/state';
import { TrackingService, TrackingVideoSource, trackingConstants } from '@frontend/sports/tracking/feature';
import { DeviceService, MediaQueryService } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { Store } from '@ngrx/store';
import { round } from 'lodash-es';
import { DynamicComponent, DynamicIoDirective } from 'ng-dynamic-component';
import { Observable, defer, map } from 'rxjs';

import { AnimationComponentLoaderService, EventAnimationBinding } from '../animation/animation-component-loader.service';
import { ExpansionPanelComponent } from '../common/expansion-panel/expansion-panel.component';
import { ComponentProxy } from '../deferred/component-loader.service';
import { ComponentProxyDirective } from '../deferred/component-proxy.directive';
import { AdaptiveLayoutService } from '../layout/adaptive-layout.service';
import { ColumnSize } from '../layout/layout.model';
import { MatchSimulationComponent } from '../match-simulation/match-simulation.component';
import { TabBarEvent } from '../tab-bar/tab-bar.models';
import { VideoStreamButtonComponent } from '../video-stream/video-stream-button/video-stream-button.component';
import { EventVideoStreamBinding, VideoStreamComponentLoaderService } from '../video-stream/video-stream-component-loader.service';
import { WidgetComponent } from '../widget/core/widget-registry.service';
import { Actions } from './actions';
import { toggleAnimations } from './animations';
import { MediaComponentMapService, MediaComponentState } from './media-component-map.service';
import { MediaComponentsModule } from './media-components.module';
import { MediaTrackingService } from './media-tracking.service';

class TrackingEventNames {
    /* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match */
    static readonly MediaModule_Animation_Tab_Click = 'MediaModule_Animation_Tab_Click';
    static readonly MediaModule_Video_Tab_Click = 'MediaModule_Video_Tab_Click';
    static readonly MediaModule_Stats_Tab_Click = (sportId: number): string => `Bet_Radar_GridStats_MediaModule_SB_ ${sportId}_Click`;
}

const tabClickTrackingEvent = {
    [EventDetailsColumnType.Video]: TrackingEventNames.MediaModule_Video_Tab_Click,
    [EventDetailsColumnType.Animation]: TrackingEventNames.MediaModule_Animation_Tab_Click,
    [EventDetailsColumnType.Stats]: (sportId: number) => TrackingEventNames.MediaModule_Stats_Tab_Click(sportId),
};

@Component({
    selector: 'ms-media',
    templateUrl: 'media.html',
    providers: [MediaComponentMapService, MediaTrackingService], // additionally registering dedicated tracking
    // service, to make sure modular-tracking.service overrides tracking.service
    animations: [toggleAnimations],
    standalone: true,
    imports: [
        DynamicIoDirective,
        DynamicComponent,
        MediaComponentsModule,
        AsyncPipe,
        NgClass,
        NgStyle,
        ExpansionPanelComponent,
        MatchSimulationComponent,
        ComponentProxyDirective,
        VideoStreamButtonComponent,
        IconCustomComponent,
    ],
})
export class MediaComponent extends WidgetComponent<never> implements OnInit, OnDestroy {
    tabType = EventDetailsColumnType;
    mediaState: MediaComponentState;
    statsCollapsed = true;
    sportType = SportConstant;
    isExpandedMediaEnabled: boolean;
    isMobileExpand: boolean;

    readonly EXPAND_BUTTON_FADE_TIME = 500; // 0.5 sec

    private mediaState$: Observable<MediaState>;
    trackingSource = '';
    private readonly trackingMap = {
        [EventDetailsColumnType.Video]: 'VideoTab',
        [EventDetailsColumnType.Stats]: 'StatsTab',
        [EventDetailsColumnType.Animation]: 'AnimationTab',
    };

    private mediaSize = new Map<string, { target?: string; width: number }[]>([
        [ColumnSize.ExtraLarge, [{ width: 550 }]],
        [ColumnSize.Large, [{ width: 440 }]],
        [ColumnSize.Default, [{ target: 'gt-mw', width: 375 }, { width: 350 }]],
    ]);

    eventStatisticsComponent$ = defer(() => import('../statistics/event-statistics.component')).pipe(map((c) => c.EventStatisticsComponent));

    eventStatisticsComponentOutputs = {
        toggle: (expand: boolean) => this.onFootballStatExpand(expand),
    };

    private readonly _doc = inject(DOCUMENT);

    constructor(
        public sitecore: Sitecore,
        public deviceService: DeviceService,
        private trackingService: TrackingService,
        private mapperService: MediaComponentMapService,
        private mediaConfig: MediaConfig,
        private scrollContainer: ScrollContainerService,
        private routerEventsService: RouterEventsService,
        private mediaService: MediaQueryService,
        private competitionLogosService: CompetitionLogosService,
        private scoreboardBackgrounds: ScoreboardBackgroundConfig,
        private shortTeamNamesService: ShortTeamNamesService,
        loader: AnimationComponentLoaderService,
        private store: Store<IMediaRootState>,
        private mediaTrackingService: MediaTrackingService,
        videoLoader: VideoStreamComponentLoaderService,
        private layoutService: AdaptiveLayoutService,
    ) {
        super();
        this.mediaState$ = store.select(mediaStateSelector);
        this.animationComponent = loader.getAnimationComponent();
        this.videoStreamComponent = videoLoader.getVideoStreamComponent();
        this.videoStreamComponent.subscribeToOutputs({ toggleMediaExpand: this.toggleMediaExpand });
    }

    sportId: number | null;
    isStatsExpanded = false;
    isLiveEvent: boolean | null;
    videoAndAnimationBgColor = {};
    statsBgColor = { 'background-image': '' };
    mediaHeight = 0;
    animationComponent: ComponentProxy<EventAnimationBinding>;
    videoStreamComponent: ComponentProxy<EventVideoStreamBinding>;
    private isEsport: boolean;
    streamingMessageHeight?: string;
    isOnEventDetails: boolean;

    ngOnInit(): void {
        this.init();

        this.mediaState$
            .pipe(
                map((mediaState: MediaState) => {
                    this.sportId = this.getSportId(mediaState);

                    const videoEvent = mediaState.video.eventId ? mediaState.eventInfos[mediaState.video.eventId].event : null;
                    this.isEsport = !!videoEvent?.sport.isEsport;

                    const statisticsEvent = mediaState.statistics.eventId ? mediaState.eventInfos[mediaState.statistics.eventId].event : null;
                    this.isLiveEvent = statisticsEvent && statisticsEvent.live;

                    return this.mapperService.mapContent(mediaState);
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((mediaComponentState) => {
                const previous = this.mediaState;
                const last = mediaComponentState;
                if (last.trackingSource !== TrackingVideoSource.Default) {
                    this.trackingSource = last.trackingSource;
                }

                const scrollToTop =
                    !previous ||
                    last.activeTab !== previous.activeTab ||
                    (last.animationEvent && last.animationEvent.id) !== (previous.animationEvent && previous.animationEvent.id) ||
                    (last.statisticsEvent && last.statisticsEvent.id) !== (previous.statisticsEvent && previous.statisticsEvent.id) ||
                    last.videoEventId !== previous.videoEventId;

                if (scrollToTop) {
                    this.scrollContainer.scrollTo(0, 0);
                }
                this.mediaState = mediaComponentState;
                this.statsCollapsed = mediaComponentState.isStatsExpanderVisible;

                this.setShortNames();
                this.setBackGroundColor();
                this.setAnimation();
                this.setVideoStream();
                this.setStreamMessageHeight();
            });

        this.routerEventsService.currentActivationEnd
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => this.onCurrentActivationEnd(event));

        this.mediaService
            .observe()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.madiaSizeChanged());

        this.setBackGroundColor();
        this.setStreamMessageHeight();
    }

    isAnimationDisabled(): boolean {
        return this.mediaState.tabs.findIndex((tab) => tab.id === EventDetailsColumnType.Animation && tab.disabled) > -1;
    }

    private setShortNames(): void {
        const event = this.mediaState.animationEvent;

        if (!event || !this.competitionLogosService.getLogoUrl(event.sport.id, event.league.id) || !event.participants.length) {
            return;
        }

        if (
            event.participants.length > 0 &&
            this.shortTeamNamesService.shouldUseShortName(event.participants[0], event.sport.id, event.league.id, ShortNameModule.Grids)
        ) {
            this.setShortName('01', 0);
        }

        if (
            event.participants.length > 1 &&
            this.shortTeamNamesService.shouldUseShortName(event.participants[1], event.sport.id, event.league.id, ShortNameModule.Grids)
        ) {
            this.setShortName('02', 1);
        }
    }

    private setShortName(player: string, index: number): void {
        const scoreboard = this.mediaState.animationEvent!.scoreboard as any;
        const participants = this.mediaState.animationEvent!.participants;

        const val = participants[index].shortName;

        if (scoreboard.playerInfo) {
            scoreboard.playerInfo.players[player].teamName = val;
        }

        if (scoreboard.score?.playerInfo) {
            scoreboard.score.playerInfo.players[player].teamName = val;
        }
    }

    ngOnDestroy(): void {
        this.toggleMediaModule(false);
    }

    tabSelected(event: TabBarEvent<EventDetailsColumnType>): void {
        const mediaTab: MediaTab = { tabId: event.active.id, trackingSource: TrackingVideoSource.Default };
        const tabIndex = this.mediaState.tabs.findIndex((tab) => tab.id === mediaTab.tabId);
        if (tabClickTrackingEvent[event.active.id]) {
            mediaTab.trackingSource = TrackingVideoSource.MediaColumn;

            const mapEventObjByTabs: Record<EventDetailsColumnType, EventModel | null> = {
                [EventDetailsColumnType.Stats]: this.mediaState.statisticsEvent,
                [EventDetailsColumnType.Animation]: this.mediaState.animationEvent,
                [EventDetailsColumnType.Video]: this.mediaState.videoEvent,
            };

            this.mediaTrackingService.trackTabClick(event.active.id, mapEventObjByTabs[event.active.id], tabIndex);
        }

        if (!event.active.disabled) {
            this.toggleCollapsedState(this.mediaState.tabs.length === 1 ? !this.mediaState.collapsed : false);
            this.updateActiveTab(mediaTab);
        }
        this.setAnimationHeight();
    }

    panelToggled(collapsed: boolean): void {
        this.mediaTrackingService.trackPanelToggle(collapsed);
        this.toggleCollapsedState(!collapsed);
        this.setAnimationHeight();
    }

    statsToggled(): void {
        this.statsCollapsed = !this.statsCollapsed;
    }

    getBackground(mediaState: MediaComponentState, type: EventDetailsColumnType): string {
        if (type === EventDetailsColumnType.Animation) {
            const animationHeight = mediaState.isAnimationAvailable ? 'animation-height' : '';

            return animationHeight + ' ' + this.mapperService.getBackgroundcolor(mediaState, type);
        } else if (type === EventDetailsColumnType.Stats) {
            const statsClass = 'stats-column';
            const collapsedClass = !this.isStatsExpanded ? 'prematch-collapsed' : '';

            return statsClass + ' ' + collapsedClass + ' ' + this.mapperService.getBackgroundcolor(mediaState, type);
        }

        return this.mapperService.getBackgroundcolor(mediaState, type);
    }

    onFootballStatExpand(expand: boolean): void {
        this.isStatsExpanded = expand;
    }

    madiaSizeChanged(): void {
        this.store.dispatch(Actions.breakpointChanged());
    }

    togglePin(isPinned: boolean): void {
        this.mediaTrackingService.trackPin(isPinned);
        this.store.dispatch(Actions.togglePin({ value: !isPinned }));
    }

    toggleMediaExpand = () => {
        this.toggleExpand(!this.mediaState.isExpanded);
    };

    toggleExpand(restoreExpandState: boolean): void {
        if (this.deviceService.isMobile) {
            this.isMobileExpand = true;
        }

        const state = this.mediaState.isExpanded ? 'Collapse' : 'Expand';
        let activeTab = this.trackingMap[this.mediaState.activeTab];

        if (!this.mediaState.videoEvent && activeTab === this.trackingMap[EventDetailsColumnType.Video]) {
            activeTab = 'MediaTab';
        }

        this.trackingService.track(trackingConstants.EVENT_MEDIA, {
            [trackingConstants.COMPONENT_SITE_ELEMENT_INTERCATIONS]: `MediaModule_${state}_${activeTab}`,
        });

        this.store.dispatch(Actions.toggleExpand({ value: restoreExpandState }));
    }

    private setAnimationHeight() {
        setTimeout(() => {
            const ele = this._doc.getElementById('mediaContainer');
            const divHeight = ele?.offsetHeight || 0;
            this.mediaHeight = divHeight;
        }, 500);
    }

    private setAnimation(): void {
        const event = this.mediaState.animationEvent;

        if (event) {
            this.animationComponent.update({ event, scoreboard: event.scoreboard, inColumn: true });
        }
    }

    private init(): void {
        this.store.dispatch(Actions.init());
    }

    private toggleMediaModule(enabled: boolean): void {
        this.store.dispatch(Actions.toggleMediaModule({ value: enabled }));
    }

    private updateActiveTab(mediaTab: MediaTab): void {
        this.store.dispatch(Actions.setActiveTab({ tab: mediaTab }));
    }

    private toggleCollapsedState(isCollapsed: boolean): void {
        this.store.dispatch(Actions.toggleCollapsedState({ value: isCollapsed }));
    }

    private setAvailableExpandLocation(): void {
        this.store.dispatch(Actions.setAvailableExpandLocation({ value: this.isExpandedMediaEnabled }));
    }

    private onCurrentActivationEnd(event: Optional<ActivationEnd>): void {
        const snapshot = event?.snapshot;
        const routeTag = snapshot?.data['tag'];
        const sportId = snapshot?.data['model']?.model?.sport?.id ?? 0;
        this.isOnEventDetails = routeTag === RouteTag.EventDetails;

        if (sportId === this.sportType.Golf && this.isOnEventDetails) {
            this.mediaState.isDefaultExpanded = true;
        } else {
            this.mediaState.isDefaultExpanded = false;
        }

        this.isExpandedMediaEnabled =
            this.mediaConfig.isExpandedMediaEnabled && this.mediaConfig.expandedMediaEnabledLocations.indexOf(routeTag) > -1;

        // Reset/restore column width
        if (
            (this.mediaState.isExpanded && !this.isExpandedMediaEnabled) ||
            (this.mediaState.restoreExpandState && this.isExpandedMediaEnabled && !this.mediaState.isExpanded) ||
            (this.mediaState.isDefaultExpanded && this.isExpandedMediaEnabled && !this.mediaState.isExpanded)
        ) {
            this.toggleExpand(true);
        }

        if (this.isExpandedMediaEnabled !== this.mediaState.isLocationAllowsExpand) {
            this.setAvailableExpandLocation();
        }

        this.setStreamMessageHeight();
    }

    private setBackGroundColor = (): void => {
        if (this.mediaState.pendingEsport) {
            this.isEsport = !!this.mediaState.pendingEsport.sport.isEsport;
            this.sportId = this.mediaState.pendingEsport.sport.id;
        }

        const isBeSpokeSport = isBespokeSport(this.sportId || 0, this.isEsport);
        const background = this.scoreboardBackgrounds.backgrounds.find((x) => x.sportId === this.sportId && isBeSpokeSport);
        if (background) {
            const gradient = { 'background-image': `radial-gradient(circle 115px at top, ${background.innerColor}, ${background.outerColor})` };

            this.videoAndAnimationBgColor = this.isEsport ? gradient : { background: background.outerColor };
            this.statsBgColor = gradient;
        } else {
            this.videoAndAnimationBgColor = { background: '' };
            this.statsBgColor = { 'background-image': '' };
        }
    };

    private getSportId(mediaState: MediaState): number {
        if (mediaState.statistics.eventId) {
            return mediaState.eventInfos[mediaState.statistics.eventId].event.sport.id;
        } else if (mediaState.animation.eventId) {
            return mediaState.eventInfos[mediaState.animation.eventId].event.sport.id;
        } else if (mediaState.video.eventId) {
            return mediaState.eventInfos[mediaState.video.eventId].event.sport.id;
        }

        return 0;
    }

    private setVideoStream(): void {
        const event = this.mediaState.videoEvent;

        if (event) {
            this.videoStreamComponent.update({
                event,
                inColumn: true,
                autoplay: this.mediaState.videoAutoplay,
                isExpanded: this.mediaState.isExpanded,
                isExpandEnabled: this.isExpandedMediaEnabled,
                trackingSource: this.trackingSource,
            });
        }
    }

    private setStreamMessageHeight(): void {
        if (this.isOnEventDetails && this.mediaState.pendingEsport) {
            const sizes = this.mediaSize.get(this.layoutService.current.rightColumnSize) || [];
            for (const size of sizes) {
                if (!size.target || this.mediaService.isActive(size.target)) {
                    this.streamingMessageHeight = `${round((size.width * 9) / 16)}px`;
                    break;
                }
            }
        } else {
            this.streamingMessageHeight = undefined;
        }
    }
}
